<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"

const { t } = useI18n()
const operationStore = useOperationStore()

const schema = yup.object().shape({
  documentsComplete: yup.boolean().test(
    "is-checked",
    "Der Arbeitsschritt kann nur mit vollständigen und formal korrekten Unterlagen abgeschlossen werden",
    yupTestOnlyOnCreate((value) => value === true)
  ),

  shipped: yup.boolean().required("Pflichtfeld"),

  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
  documentsCompleteDate: yup
    .string()
    .when("documentsComplete", {
      is: true,
      then: (s) => s.required("Pflichtfeld"),
      otherwise: (s) => s.strip(),
    })
    .test("is-date", "Ungültiges Datum", (value?: string) => {
      return value ? isDateValid(value) : true
    }),

  automaticRecognition: yup.boolean().when("documentsComplete", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  otherProcedures: yup.boolean().required("Pflichtfeld"),
  equivalenceAssessable: yup.boolean(),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const setDefaultValues = async () => {
  if (state.automaticRecognition === undefined) {
    state.automaticRecognition = false
  }
  if (state.equivalenceAssessable === undefined) {
    state.equivalenceAssessable = true
  }
  await nextTick()
  operationStore.unsavedChanges = false
}

watch(
  () => state.documentsComplete,
  (value) => {
    if (value === false) {
      state.documentsCompleteDate = undefined
      state.automaticRecognition = false
    }
  }
)

onMounted(() => {
  setDefaultValues()
})
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Vollständigkeit"
      />
    </template>

    <IffContentSection headline="Vollständigkeit prüfen" :is-last-child="false">
      <IffDualRadioGroup
        name="documentsComplete"
        v-model="state.documentsComplete"
        legend="Sind die Unterlagen vollständig und formal korrekt?"
        required
        aria-controls="documents-completion-date-region other-procedures-region"
      />
      <div id="documents-completion-date-region" aria-live="polite">
        <IffTransitionHeight>
          <div v-if="state.documentsComplete">
            <UFormGroup
              name="documentsCompleteDate"
              label="Datum der Vollständigkeit der Unterlagen"
              required
              class="max-w-[355px]"
            >
              <template #hint>
                <IffIconTooltip
                  text="BQFG: Wert wird in Datum der Vollständigkeit der vorzulegenden Unterlagen (e07) übernommen.

                  Frist zur Bearbeitung wird wie folgt berechnet:
                - Datum der Vollständigkeit der Unterlagen plus 3 Monate bei EU/EWR/Schweiz sowie plus 4 Monate bei Drittstaaten.
                - Bei Anträgen im Rahmen FEG: plus 2 Monate für allen Antragstypen."
                />
                <UButton
                  @click="
                    state.documentsCompleteDate = new Date()
                      .toISOString()
                      .slice(0, 10)
                  "
                  icon="i-ic-outline-access-time"
                  size="sm"
                  color="gray"
                  variant="ghost"
                  :trailing="false"
                  aria-label="Aktuelles Datum wählen"
                  title="Aktuelles Datum wählen"
                />
              </template>
              <UInput
                v-model="state.documentsCompleteDate"
                type="date"
                aria-required="true"
                class="whitespace-normal max-w-[150px]"
              />
            </UFormGroup>

            <IffDualRadioGroup
              name="automaticRecognition"
              v-model="state.automaticRecognition"
              legend="Automatische Anerkennung?"
              required
            />
          </div>
        </IffTransitionHeight>
      </div>
      <div id="other-procedures-region" aria-live="polite">
        <IffTransitionHeight>
          <IffDualRadioGroup
            v-if="state.documentsComplete !== undefined"
            name="otherProcedures"
            v-model="state.otherProcedures"
            :legend="`Sonstige Verfahren (§ 18 BQFG ${t('custom.common.federal-state-code')})?`"
            required
          />
        </IffTransitionHeight>
      </div>
    </IffContentSection>
    <IffContentSection
        headline="Versand Vollständigkeitsschreiben"
        :is-last-child="false"
    >
      <UFormGroup name="shipped">
        <IffDualRadioGroup
            legend="Ist das Vollständigkeitsschreiben erforderlich und ist der Versand an die antragstellende Person erfolgt?"
            v-model="state.shipped"
            required
            aria-required="true"
            aria-controls="shipping-confirmation-date-region"
        />
      </UFormGroup>
      <div id="shipping-confirmation-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
              name="shippingConfirmationDate"
              label="Datum Eingangsbestätigung"
              v-if="state.shipped"
              required
              class="max-w-[270px]"
          >
            <template #hint>
              <IffIconTooltip
                  text="BQFG: Wert wird in Datum der Empfangsbestätigung (e01) übernommen."
              />

              <UButton
                  @click="
                  state.shippingConfirmationDate = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                  icon="i-ic-outline-access-time"
                  size="sm"
                  color="gray"
                  variant="ghost"
                  :trailing="false"
                  aria-label="Aktuelles Datum wählen"
                  title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
                v-model="state.shippingConfirmationDate"
                type="date"
                aria-required="true"
                class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
    <IffContentSection headline="Gleichwertigkeitsprüfung" :is-last-child="true">
      <IffDualRadioGroup
          name="equivalenceAssessable"
          v-model="state.equivalenceAssessable"
          legend="Ist eine Gleichwertigkeitsprüfung möglich?"
          aria-controls="equivalence-assessable"
      />
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
